import type {
  HRAttendanceStatus,
  HRDivisor,
  HRImportRow,
  HRItemKind,
  HRLeaveType,
  HRPayoutMethod,
  HRRecoveryPlan,
  HRRule,
  HRRuleKind,
  HRRunLine,
  HRRun,
  HRYearEndPolicy,
} from '@/types/hr'

// Staff & Payroll (F-HR-N1..N6): labels, the register's codes, CSV in and out,
// and the printable payslip. Pure — tested in hr.test.ts.

export const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const
export const WEEKDAYS_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const

export const STATUS_LABEL: Record<HRAttendanceStatus, string> = {
  present: 'Present',
  late: 'Late',
  half_day: 'Half day',
  absent: 'Absent',
  leave: 'Leave',
  weekly_off: 'Off',
  holiday: 'Holiday',
}

/** The register's one-to-three letter code, Excel-sheet style. */
export function statusCode(status: HRAttendanceStatus | null | undefined, leaveCode?: string): string {
  switch (status) {
    case 'present':
      return 'P'
    case 'late':
      return 'L'
    case 'half_day':
      return 'H'
    case 'absent':
      return 'A'
    case 'leave':
      return leaveCode || 'LV'
    case 'weekly_off':
      return 'O'
    case 'holiday':
      return 'HO'
    default:
      return ''
  }
}

/** Tailwind classes for a status chip / cell. Absent is the only loud one. */
export function statusTone(status: HRAttendanceStatus | null | undefined): string {
  switch (status) {
    case 'absent':
      return 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300'
    case 'late':
    case 'half_day':
      return 'bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300'
    case 'leave':
      return 'bg-sky-50 text-sky-800 dark:bg-sky-950/40 dark:text-sky-300'
    case 'weekly_off':
    case 'holiday':
      return 'bg-muted text-muted-foreground'
    case 'present':
      return 'text-foreground'
    default:
      return 'text-muted-foreground'
  }
}

export const DIVISOR_LABEL: Record<HRDivisor, string> = {
  '30': '÷ 30 (same every month)',
  month_days: '÷ days in the month',
  working_days: '÷ working days (weekly offs excluded)',
}

export const METHOD_LABEL: Record<HRPayoutMethod, string> = {
  bank: 'Bank transfer',
  cash: 'Cash (office)',
  wallet: 'JazzCash / Easypaisa',
}

export const RECOVERY_LABEL: Record<HRRecoveryPlan, string> = {
  next_payroll: 'All from next payroll',
  installments: 'Monthly instalments',
  fixed_monthly: 'Fixed amount each month',
}

export const YEAR_END_LABEL: Record<HRYearEndPolicy, string> = {
  lapse: 'Unused days lapse',
  carry: 'Carry over to next year',
  encash: 'Pay out unused days',
  carry_then_encash: 'Carry some, pay out the rest',
}

export const ITEM_KIND_LABEL: Record<HRItemKind, string> = {
  bonus: 'Bonus',
  deduction: 'Deduction',
  advance_recovery: 'Advance recovery',
  leave_encashment: 'Leave paid out',
}

/** "Casual", "Sick (unpaid)" — without saying "unpaid" twice for a type already called Unpaid. */
export function leaveTypeLabel(t: Pick<HRLeaveType, 'name' | 'paid'>): string {
  return t.paid || /unpaid/i.test(t.name) ? t.name : `${t.name} (unpaid)`
}

export const RULE_KIND_LABEL: Record<HRRuleKind, string> = {
  lates_to_day: 'Lates add up to a day’s cut',
  late_flat_fine: 'Fine for each late',
  absent_penalty: 'Extra cut for no-shows',
  perfect_attendance_bonus: 'Perfect attendance bonus',
  fixed_allowance: 'Fixed monthly allowance',
}

const rs = (n: number) => `Rs ${Math.round(n).toLocaleString('en-PK')}`

/** One line saying what a rule does, for the rules list. */
export function ruleDescription(rule: Pick<HRRule, 'kind' | 'params'>): string {
  const p = rule.params || {}
  switch (rule.kind) {
    case 'lates_to_day':
      return `Every ${p.every || 0} lates in a month = 1 day’s pay cut`
    case 'late_flat_fine':
      return `${rs(p.amount || 0)} for each late over ${p.grace_minutes || 0} min`
    case 'absent_penalty': {
      const parts = []
      if (p.amount) parts.push(rs(p.amount))
      if (p.days) parts.push(`${p.days} extra day’s pay`)
      return `${parts.join(' + ')} for each day absent without leave`
    }
    case 'perfect_attendance_bonus':
      return `${rs(p.amount || 0)} with no absences${p.max_lates ? ` and at most ${p.max_lates} lates` : ' and no lates'}${p.allow_paid_leave ? ' (paid leave allowed)' : ''}`
    case 'fixed_allowance':
      return `${rs(p.amount || 0)} a month, prorated for part months`
  }
}

/** "2026-10" → "October 2026". */
export function monthLabel(month: string): string {
  const [y, m] = month.split('-').map(Number)
  if (!y || !m) return month
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' })
}

/** "2026-10-05" → "Mon 5 Oct". */
export function dayLabel(date: string, withYear = false): string {
  const d = new Date(`${date}T12:00:00Z`)
  if (Number.isNaN(d.getTime())) return date
  return d.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    ...(withYear ? { year: 'numeric' } : {}),
    timeZone: 'UTC',
  })
}

export function todayISO(now = new Date()): string {
  const y = now.getFullYear()
  const m = String(now.getMonth() + 1).padStart(2, '0')
  const d = String(now.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

export function addDaysISO(date: string, n: number): string {
  const d = new Date(`${date}T12:00:00Z`)
  d.setUTCDate(d.getUTCDate() + n)
  return d.toISOString().slice(0, 10)
}

export function addMonths(month: string, n: number): string {
  const [y, m] = month.split('-').map(Number)
  const d = new Date(Date.UTC(y, m - 1 + n, 1))
  return d.toISOString().slice(0, 7)
}

export function days(n: number): string {
  const v = Math.round(n * 2) / 2
  return `${v} ${v === 1 ? 'day' : 'days'}`
}

// ── CSV ─────────────────────────────────────────────────────────────────────

function csvCell(v: unknown): string {
  const s = v == null ? '' : String(v)
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

export function toCSV(rows: unknown[][]): string {
  return rows.map((r) => r.map(csvCell).join(',')).join('\r\n')
}

/** Saves a CSV in the browser (Excel opens it). */
export function downloadCSV(filename: string, rows: unknown[][]): void {
  const blob = new Blob(['﻿' + toCSV(rows)], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

/** RFC-4180-ish parser: quotes, doubled quotes, CRLF, commas or tabs. */
export function parseCSV(text: string): string[][] {
  const src = text.replace(/^﻿/, '')
  const delim = src.split(/\r?\n/, 1)[0].includes('\t') && !src.split(/\r?\n/, 1)[0].includes(',') ? '\t' : ','
  const rows: string[][] = []
  let row: string[] = []
  let cell = ''
  let quoted = false
  for (let i = 0; i < src.length; i++) {
    const c = src[i]
    if (quoted) {
      if (c === '"' && src[i + 1] === '"') {
        cell += '"'
        i++
      } else if (c === '"') {
        quoted = false
      } else {
        cell += c
      }
      continue
    }
    if (c === '"') quoted = true
    else if (c === delim) {
      row.push(cell)
      cell = ''
    } else if (c === '\n' || c === '\r') {
      if (c === '\r' && src[i + 1] === '\n') i++
      row.push(cell)
      rows.push(row)
      row = []
      cell = ''
    } else cell += c
  }
  if (cell !== '' || row.length) {
    row.push(cell)
    rows.push(row)
  }
  return rows.filter((r) => r.some((c) => c.trim() !== ''))
}

const HEADER_ALIASES: Record<string, string[]> = {
  full_name: ['name', 'full name', 'full_name', 'employee', 'employee name', 'staff name'],
  phone: ['phone', 'mobile', 'phone number', 'contact', 'whatsapp'],
  designation: ['designation', 'title', 'job', 'position', 'role'],
  department: ['department', 'dept', 'section'],
  pay_type: ['pay type', 'pay_type', 'type', 'salary type'],
  rate: ['salary', 'rate', 'pay', 'monthly salary', 'daily wage', 'wage', 'basic'],
  join_date: ['join date', 'joining date', 'join_date', 'date of joining', 'doj', 'start date'],
  weekly_off: ['weekly off', 'off day', 'weekly_off', 'day off', 'off days'],
  opening_due: ['salary due', 'opening due', 'opening_due', 'due', 'owed'],
  opening_advance: ['advance', 'advance owed', 'opening advance', 'opening_advance', 'loan'],
  username: ['username', 'login', 'pos login'],
}

function normHeader(h: string): string {
  return h.trim().toLowerCase().replace(/[_\s]+/g, ' ').replace(/[()]/g, '')
}

function parseMoney(s: string): number | undefined {
  const t = s.replace(/rs\.?|pkr|,|\s/gi, '')
  if (t === '') return undefined
  const n = Number(t)
  return Number.isFinite(n) ? n : NaN
}

/** Accepts 2025-03-01, 01/03/2025 or 1-3-2025 (day first, as in Pakistan). */
export function parseDateLoose(s: string): string | null {
  const t = s.trim()
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return t
  const m = t.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})$/)
  if (!m) return null
  const day = Number(m[1])
  const month = Number(m[2])
  let year = Number(m[3])
  if (year < 100) year += 2000
  if (month < 1 || month > 12 || day < 1 || day > 31) return null
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

export function parseWeekdays(s: string): number[] | null {
  const out: number[] = []
  for (const part of s.split(/[;,/&+]| and /i).map((p) => p.trim().toLowerCase()).filter(Boolean)) {
    const i = WEEKDAYS_LONG.findIndex((d) => d.toLowerCase().startsWith(part.slice(0, 3)))
    if (i < 0 || part.length < 3) return null
    if (!out.includes(i)) out.push(i)
  }
  return out
}

export interface ImportParse {
  rows: HRImportRow[]
  errors: string[]
  columns: Record<string, string>
}

/**
 * Turns the tenant's spreadsheet into import rows. Headers are matched
 * loosely ("Joining Date", "DOJ", "Salary"…); a column named after a leave
 * type code or name ("CL", "Casual leave used") is leave already taken.
 */
export function importRowsFromTable(table: string[][], leaveTypes: Pick<HRLeaveType, 'code' | 'name'>[]): ImportParse {
  const errors: string[] = []
  const columns: Record<string, string> = {}
  if (table.length < 2) return { rows: [], errors: ['The file needs a header row and at least one person'], columns }
  const header = table[0].map(normHeader)
  const idx: Record<string, number> = {}
  header.forEach((h, i) => {
    for (const [field, aliases] of Object.entries(HEADER_ALIASES)) {
      if (idx[field] === undefined && aliases.includes(h)) {
        idx[field] = i
        columns[table[0][i]] = field
        return
      }
    }
    for (const lt of leaveTypes) {
      const code = lt.code.toLowerCase()
      const name = lt.name.toLowerCase()
      if ([code, `${code} used`, `${name}`, `${name} leave`, `${name} used`, `${name} leave used`].includes(h)) {
        idx[`leave:${lt.code}`] = i
        columns[table[0][i]] = `${lt.name} leave taken`
        return
      }
    }
  })
  if (idx.full_name === undefined) errors.push('No "Name" column')
  if (idx.rate === undefined) errors.push('No "Salary" (or "Rate") column')
  if (idx.join_date === undefined) errors.push('No "Joining date" column')
  if (errors.length) return { rows: [], errors, columns }

  const rows: HRImportRow[] = []
  table.slice(1).forEach((r, n) => {
    const at = (f: string) => (idx[f] === undefined ? '' : (r[idx[f]] ?? '').trim())
    const line = n + 2
    const name = at('full_name')
    if (!name) return
    const rate = parseMoney(at('rate'))
    const join = parseDateLoose(at('join_date'))
    if (rate === undefined || Number.isNaN(rate)) errors.push(`Row ${line} (${name}): salary "${at('rate')}" isn't a number`)
    if (!join) errors.push(`Row ${line} (${name}): joining date "${at('join_date')}" isn't a date`)
    const row: HRImportRow = { full_name: name, join_date: join || '', rate: rate && !Number.isNaN(rate) ? rate : undefined }
    if (at('phone')) row.phone = at('phone')
    if (at('designation')) row.designation = at('designation')
    if (at('department')) row.department = at('department')
    if (at('pay_type')) row.pay_type = at('pay_type')
    if (at('username')) row.username = at('username')
    if (at('weekly_off')) {
      const w = parseWeekdays(at('weekly_off'))
      if (w === null) errors.push(`Row ${line} (${name}): weekly off "${at('weekly_off')}" isn't a weekday`)
      else row.weekly_off_days = w
    }
    for (const f of ['opening_due', 'opening_advance'] as const) {
      const v = parseMoney(at(f))
      if (v !== undefined) {
        if (Number.isNaN(v)) errors.push(`Row ${line} (${name}): "${at(f)}" isn't a number`)
        else row[f] = v
      }
    }
    for (const lt of leaveTypes) {
      const v = at(`leave:${lt.code}`)
      if (v === '') continue
      const nDays = Number(v)
      if (!Number.isFinite(nDays)) errors.push(`Row ${line} (${name}): ${lt.name} leave "${v}" isn't a number`)
      else if (nDays) row.leave_used = { ...(row.leave_used || {}), [lt.code]: nDays }
    }
    rows.push(row)
  })
  if (!rows.length && !errors.length) errors.push('No people found under the header row')
  return { rows, errors, columns }
}

export const IMPORT_TEMPLATE: string[][] = [
  ['Name', 'Phone', 'Designation', 'Department', 'Pay type', 'Salary', 'Joining date', 'Weekly off', 'Salary due', 'Advance owed', 'CL', 'SL', 'AL'],
  ['Ali Khan', '03001234567', 'Cook', 'Kitchen', 'monthly', '45000', '01/03/2024', 'Sunday', '0', '5000', '2', '0', '0'],
  ['Bilal Ahmed', '03007654321', 'Dishwasher', 'Kitchen', 'daily', '1500', '15/08/2025', 'Friday', '3000', '0', '0', '1', '0'],
]

// ── Payslip ─────────────────────────────────────────────────────────────────

function esc(s: string | null | undefined): string {
  return (s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] as string)
}

/**
 * A self-contained printable payslip (one or many people). Opened in a new
 * window and printed; plain HTML so it prints the same everywhere.
 */
export function payslipHTML(opts: {
  business: string
  run: Pick<HRRun, 'month' | 'status'>
  lines: HRRunLine[]
  money: (n: number) => string
}): string {
  const { business, run, lines, money } = opts
  const slips = lines
    .map((l) => {
      const d = l.detail
      const kept = l.items.filter((i) => i.state !== 'removed')
      const row = (label: string, value: string, cls = '') => `<tr class="${cls}"><td>${esc(label)}</td><td class="r">${value}</td></tr>`
      const payLine =
        d.pay_type === 'daily'
          ? `${esc(String(d.worked_days))} days worked × ${money(d.rate)}`
          : `${money(d.rate)} a month${d.employed_days < d.days_in_period ? ` · ${d.employed_days} of ${d.days_in_period} days` : ''}`
      return `<section class="slip">
  <header><div><h1>${esc(business)}</h1><p>Payslip · ${esc(monthLabel(run.month))}${run.status !== 'finalized' ? ' · DRAFT' : ''}</p></div>
  <div class="who"><strong>${esc(d.full_name)}</strong><span>${esc([d.designation, d.department].filter(Boolean).join(' · '))}</span></div></header>
  <table class="days"><tr><td>Worked</td><td>${d.worked_days}</td><td>Absent</td><td>${d.absent_days}</td><td>Late</td><td>${d.late_count}</td></tr>
  <tr><td>Paid leave</td><td>${d.paid_leave_days}</td><td>Unpaid leave</td><td>${d.unpaid_leave_days}</td><td>Off / holidays</td><td>${d.off_days + d.holiday_days}</td></tr></table>
  <table class="money">
    ${row(`Pay (${payLine})`, money(l.gross))}
    ${l.absence_deduction ? row(`Absence (${d.absent_days + d.unpaid_leave_days} days × ${money(d.per_day)})`, '−' + money(l.absence_deduction)) : ''}
    ${kept
      .map((i) => row(i.reason, (i.kind === 'bonus' || i.kind === 'leave_encashment' ? '' : '−') + money(i.amount)))
      .join('')}
    ${row('Net pay', money(l.net), 'total')}
    ${l.paid ? row('Paid', money(l.paid)) : ''}
  </table>
  <footer><span>Received by: ____________________</span><span>Date: __________</span></footer>
</section>`
    })
    .join('\n')
  return `<!doctype html><html><head><meta charset="utf-8"><title>Payslips · ${esc(monthLabel(run.month))}</title>
<style>
  body{font:13px/1.45 system-ui,-apple-system,Segoe UI,Roboto,sans-serif;color:#111;margin:24px}
  .slip{border:1px solid #ccc;border-radius:8px;padding:16px 18px;margin:0 auto 18px;max-width:640px;page-break-inside:avoid}
  header{display:flex;justify-content:space-between;gap:16px;border-bottom:1px solid #ddd;padding-bottom:8px;margin-bottom:10px}
  h1{font-size:16px;margin:0} header p{margin:2px 0 0;color:#555}
  .who{text-align:right;display:flex;flex-direction:column} .who span{color:#555}
  table{width:100%;border-collapse:collapse} td{padding:4px 0}
  .days td{color:#444;width:16%} .days td:nth-child(even){font-weight:600;color:#111}
  .money{margin-top:8px;border-top:1px solid #eee} .money td{border-bottom:1px solid #f0f0f0} .r{text-align:right;white-space:nowrap}
  .total td{font-weight:700;font-size:14px;border-top:2px solid #111}
  footer{display:flex;justify-content:space-between;margin-top:22px;color:#555}
  @media print{body{margin:0}.slip{border-color:#999}}
</style></head><body>${slips}</body></html>`
}
